import * as THREE from 'three'
import { cloneGlbHierarchyUserDataMeta, type GlbHierarchyNode, type GlbHierarchyNodeMeta } from '../../imported-model/loaders/glb'
import type {
  ComposedAssemblyResult,
  ImportedTopologySource,
  SceneBindingIndex,
  SelectionIndex,
  SelectionTarget,
} from '../../imported-model/loaders/types'
import { cloneWithSharedGeometry } from '../../imported-model/loaders/part'
import { activateImportedModelTopologyHosts } from '../../imported-model/topology/imported-model-topology-runtime'
import {
  ImportedModelObjectMetadataService,
  type ImportedModelObjectMetadata,
  type ImportedModelSelectionTargetMetadata,
} from '../../imported-model/metadata'
import type { EntityHighlightTarget } from '../../imported-model/types/entity-highlight-target'
import { addObject3DToParent } from '../../utils/scene-object/addObject3DToParent'

/**
 * `.puppy` 子装配体运行时结果创建输入。
 */
export interface PuppyScopedAssemblyResultInput {
  /** 完整 `.puppy` 装配体导入结果。 */
  sourceResult: ComposedAssemblyResult
  /** 当前 tab 对应的装配体选择代理 Object3D UUID。 */
  scopeObjectId: string
  /** `.puppy` 自建拓扑源，用于重新激活克隆子树的点线面交互。 */
  topologySource: ImportedTopologySource
}

/**
 * 按 UUID 收集 Object3D，供 hierarchy 和 metadata 重新绑定运行时对象。
 *
 * @param root 遍历根节点。
 * @returns Object3D UUID 到对象实例的索引。
 */
function collectObjectByUuid(root: THREE.Object3D): Map<string, THREE.Object3D> {
  const objectByUuid = new Map<string, THREE.Object3D>()
  root.traverse((object) => {
    objectByUuid.set(object.uuid, object)
  })
  return objectByUuid
}

/**
 * 在 hierarchy 中按对象 ID 或选择代理 ID 查找子树根。
 *
 * @param node 当前 hierarchy 节点。
 * @param objectId 目标 Object3D UUID。
 * @returns 命中的 hierarchy 节点；未命中时返回 null。
 */
function findHierarchyNodeByObjectId(node: GlbHierarchyNode, objectId: string): GlbHierarchyNode | null {
  if (node.objectId === objectId || node.meta?.selectionObjectId === objectId) {
    return node
  }

  for (const child of node.children) {
    const found = findHierarchyNodeByObjectId(child, objectId)
    if (found) {
      return found
    }
  }
  return null
}

/**
 * 在 Object3D 树中查找指定 UUID。
 *
 * @param root 遍历根节点。
 * @param objectId 目标 Object3D UUID。
 * @returns 命中的对象；未命中时返回 null。
 */
function findObjectByUuid(root: THREE.Object3D, objectId: string): THREE.Object3D | null {
  let matched: THREE.Object3D | null = null
  root.traverse((object) => {
    if (!matched && object.uuid === objectId) {
      matched = object
    }
  })
  return matched
}

/**
 * 将源对象树和克隆对象树按结构配对，建立旧 UUID 到新 UUID 的映射。
 *
 * @param source 源对象。
 * @param cloned 克隆对象。
 * @param uuidMap 旧 UUID 到新 UUID 的映射。
 */
function collectCloneUuidMap(
  source: THREE.Object3D,
  cloned: THREE.Object3D,
  uuidMap: Map<string, string>,
): void {
  uuidMap.set(source.uuid, cloned.uuid)
  source.children.forEach((sourceChild, index) => {
    const clonedChild = cloned.children[index]
    if (clonedChild) {
      collectCloneUuidMap(sourceChild, clonedChild, uuidMap)
    }
  })
}

/**
 * 将旧 Object3D UUID 映射为克隆后的 UUID。
 *
 * @param objectId 原始 Object3D UUID。
 * @param uuidMap 旧 UUID 到新 UUID 的映射。
 * @returns 克隆后的 UUID；无法映射时返回原值。
 */
function remapOptionalObjectId(
  objectId: string | null | undefined,
  uuidMap: Map<string, string>,
): string | null | undefined {
  if (objectId === null || objectId === undefined) {
    return objectId
  }
  return uuidMap.get(objectId) ?? objectId
}

/**
 * 重写高亮目标内的运行时对象 ID。
 *
 * @param targets 源高亮目标。
 * @param uuidMap 旧 UUID 到新 UUID 的映射。
 * @returns 克隆后的高亮目标。
 */
function remapHighlightTargets(
  targets: EntityHighlightTarget[] | undefined,
  uuidMap: Map<string, string>,
): EntityHighlightTarget[] | undefined {
  return targets?.map((target) => ({
    ...target,
    objectId: uuidMap.get(target.objectId) ?? target.objectId,
    primitiveIndices: target.primitiveIndices ? [...target.primitiveIndices] : undefined,
  }))
}

/**
 * 重写 body 高亮目标内的运行时对象 ID。
 *
 * @param targetsByBodyId 源 body 高亮目标索引。
 * @param uuidMap 旧 UUID 到新 UUID 的映射。
 * @returns 克隆后的 body 高亮目标索引。
 */
function remapBodyHighlightTargetsById(
  targetsByBodyId: Record<string, EntityHighlightTarget[]> | undefined,
  uuidMap: Map<string, string>,
): Record<string, EntityHighlightTarget[]> | undefined {
  if (!targetsByBodyId) {
    return undefined
  }

  return Object.fromEntries(
    Object.entries(targetsByBodyId).map(([bodyId, targets]) => [
      bodyId,
      remapHighlightTargets(targets, uuidMap) ?? [],
    ]),
  )
}

/**
 * 收集 hierarchy 子树内允许使用的装配语义节点 ID。
 *
 * @param node hierarchy 子树根。
 * @param target 输出集合。
 */
function collectAssemblyNodeIdsFromHierarchy(
  node: GlbHierarchyNode,
  target: Set<string>,
): void {
  if (node.meta?.assemblyNodeId) {
    target.add(node.meta.assemblyNodeId)
  }
  node.children.forEach((child) => collectAssemblyNodeIdsFromHierarchy(child, target))
}

/**
 * 收集 Object3D 子树 metadata 中声明的装配语义节点 ID。
 *
 * @param root Object3D 子树根。
 * @param target 输出集合。
 */
function collectAssemblyNodeIdsFromMetadata(
  root: THREE.Object3D,
  target: Set<string>,
): void {
  root.traverse((object) => {
    const metadata = ImportedModelObjectMetadataService.peekMetadata(object)
    if (metadata?.assemblyNodeId) {
      target.add(metadata.assemblyNodeId)
    }
    if (metadata?.selectionTarget?.assemblyNodeId) {
      target.add(metadata.selectionTarget.assemblyNodeId)
    }
  })
}

/**
 * 克隆 scoped 子树内仍有效的选择目标。
 *
 * @param target 原始选择目标。
 * @param scopedAssemblyNodeIds 当前 scoped 子树允许访问的装配节点 ID。
 * @returns 有效目标副本；越界目标返回 undefined。
 */
function cloneScopedSelectionTarget(
  target: ImportedModelSelectionTargetMetadata | undefined,
  scopedAssemblyNodeIds: ReadonlySet<string>,
): ImportedModelSelectionTargetMetadata | undefined {
  if (!target?.assemblyNodeId || !scopedAssemblyNodeIds.has(target.assemblyNodeId)) {
    return undefined
  }
  return {
    assemblyNodeId: target.assemblyNodeId,
    partUuid: target.partUuid,
    primitiveId: target.primitiveId,
    stepFaceId: target.stepFaceId,
  }
}

/**
 * 裁剪 bodyBindingId 到选择目标的覆盖映射。
 *
 * 设计说明：
 * scoped 子装配 tab 会把 Object3D 子树从完整装配中克隆出来，父级语义已经不存在。
 * body binding 覆盖目标如果仍指向完整装配外的节点，拓扑面选择和拖动会穿回旧空间。
 * 因此这里只保留当前 scoped 子树内的目标，其他目标交给对象自身 selectionTarget 兜回
 * 本 scoped 子树，避免把组件边界错误扩散到 Mate 求解链路。
 *
 * @param targetsByBodyBindingId 原始覆盖映射。
 * @param scopedAssemblyNodeIds 当前 scoped 子树允许访问的装配节点 ID。
 * @returns 裁剪后的覆盖映射。
 */
function cloneScopedBodyBindingSelectionTargetsById(
  targetsByBodyBindingId: Record<string, ImportedModelSelectionTargetMetadata> | undefined,
  scopedAssemblyNodeIds: ReadonlySet<string>,
): Record<string, ImportedModelSelectionTargetMetadata> | undefined {
  if (!targetsByBodyBindingId) {
    return undefined
  }

  const scopedTargets: Record<string, ImportedModelSelectionTargetMetadata> = {}
  Object.entries(targetsByBodyBindingId).forEach(([bodyBindingId, target]) => {
    const scopedTarget = cloneScopedSelectionTarget(target, scopedAssemblyNodeIds)
    if (scopedTarget) {
      scopedTargets[bodyBindingId] = scopedTarget
    }
  })
  return Object.keys(scopedTargets).length > 0 ? scopedTargets : undefined
}

/**
 * 克隆并重写 hierarchy meta 中的运行时对象引用。
 *
 * @param meta 源 hierarchy meta。
 * @param uuidMap 旧 UUID 到新 UUID 的映射。
 * @returns 克隆后的 hierarchy meta。
 */
function cloneScopedHierarchyMeta(
  meta: GlbHierarchyNodeMeta | undefined,
  uuidMap: Map<string, string>,
): GlbHierarchyNodeMeta | undefined {
  if (!meta) {
    return undefined
  }

  const cloned = cloneGlbHierarchyUserDataMeta(meta)
  return {
    ...cloned,
    selectionObjectId: remapOptionalObjectId(cloned.selectionObjectId, uuidMap) ?? cloned.selectionObjectId,
    highlightTargets: remapHighlightTargets(cloned.highlightTargets, uuidMap),
    bodyHighlightTargetsById: remapBodyHighlightTargetsById(cloned.bodyHighlightTargetsById, uuidMap),
    materialNames: [...(cloned.materialNames ?? [])],
  }
}

/**
 * 克隆并重写导入模型对象 metadata 中的运行时对象引用。
 *
 * @param metadata 源 metadata。
 * @param uuidMap 旧 UUID 到新 UUID 的映射。
 * @returns 克隆后的 metadata。
 */
function cloneScopedObjectMetadata(
  metadata: ImportedModelObjectMetadata | undefined,
  uuidMap: Map<string, string>,
  scopedAssemblyNodeIds: ReadonlySet<string>,
): ImportedModelObjectMetadata | undefined {
  if (!metadata) {
    return undefined
  }

  return {
    ...metadata,
    hierarchy: metadata.hierarchy
      ? {
        ...metadata.hierarchy,
        parentObjectId: remapOptionalObjectId(metadata.hierarchy.parentObjectId, uuidMap) ?? null,
        meta: cloneScopedHierarchyMeta(metadata.hierarchy.meta, uuidMap),
      }
      : undefined,
    selectionProxyObjectId: remapOptionalObjectId(metadata.selectionProxyObjectId, uuidMap) ?? undefined,
    selectionTarget: metadata.selectionTarget
      ? { ...metadata.selectionTarget }
      : undefined,
    bodyBindingSelectionTargetsById: cloneScopedBodyBindingSelectionTargetsById(
      metadata.bodyBindingSelectionTargetsById,
      scopedAssemblyNodeIds,
    ),
    fileInfo: metadata.fileInfo
      ? { ...metadata.fileInfo }
      : undefined,
  }
}

/**
 * 重写克隆 Object3D 树中的 imported-model metadata。
 *
 * @param clonedRoot 克隆对象根。
 * @param uuidMap 旧 UUID 到新 UUID 的映射。
 */
function rewriteScopedObjectMetadata(
  clonedRoot: THREE.Object3D,
  uuidMap: Map<string, string>,
  scopedAssemblyNodeIds: ReadonlySet<string>,
): void {
  clonedRoot.traverse((object) => {
    const sourceMetadata = ImportedModelObjectMetadataService.peekMetadata(object)
    const remappedMetadata = cloneScopedObjectMetadata(sourceMetadata, uuidMap, scopedAssemblyNodeIds)
    if (object === clonedRoot && remappedMetadata?.hierarchy) {
      /**
       * 子装配体 tab 的根对象已经脱离父装配上下文，metadata 里的父节点引用必须同步归零。
       * 否则拖拽、固定/浮动和设计树反查仍可能追到完整装配体中的旧父节点。
       */
      remappedMetadata.hierarchy = {
        ...remappedMetadata.hierarchy,
        parentObjectId: null,
        depth: 0,
      }
    }
    if (remappedMetadata) {
      ImportedModelObjectMetadataService.writeMetadata(object, remappedMetadata)
    }
  })
}

/**
 * 克隆并重写 hierarchy 子树，使其根节点成为当前 tab 的业务根。
 *
 * @param input 克隆输入。
 * @returns 当前 tab 使用的 hierarchy。
 */
function cloneScopedHierarchy(input: {
  node: GlbHierarchyNode
  parentObjectId: string | null
  parentPath: string
  depth: number
  uuidMap: Map<string, string>
}): GlbHierarchyNode {
  const objectId = input.uuidMap.get(input.node.objectId) ?? input.node.objectId
  const path = input.parentPath ? `${input.parentPath}/${input.node.name}` : input.node.name
  const children = input.node.children.map((child) => cloneScopedHierarchy({
    node: child,
    parentObjectId: objectId,
    parentPath: path,
    depth: input.depth + 1,
    uuidMap: input.uuidMap,
  }))

  return {
    ...input.node,
    objectId,
    parentObjectId: input.parentObjectId,
    path,
    depth: input.depth,
    meta: cloneScopedHierarchyMeta(input.node.meta, input.uuidMap),
    children,
  }
}

/**
 * 从 `.puppy` imported-model metadata 收集选择索引和场景绑定。
 *
 * @param root 运行时结果根节点。
 * @returns 选择索引和场景绑定。
 */
export function createPuppyRuntimeIndexesFromMetadata(root: THREE.Object3D): {
  selectionIndex: SelectionIndex
  sceneBindings: SceneBindingIndex
} {
  const objectByUuid = collectObjectByUuid(root)
  const selectionIndex: SelectionIndex = { byObject3DUuid: {} }
  const sceneBindings: SceneBindingIndex = {
    byAssemblyNodeId: {},
    byObject3DUuid: {},
  }
  const bindingIdsByAssemblyNodeId = new Map<string, Set<string>>()

  root.traverse((object) => {
    const metadata = ImportedModelObjectMetadataService.peekMetadata(object)
    const selectionTarget = metadata?.selectionTarget as SelectionTarget | undefined
    if (selectionTarget?.assemblyNodeId) {
      selectionIndex.byObject3DUuid[object.uuid] = { ...selectionTarget }
    }

    const assemblyNodeId = metadata?.assemblyNodeId ?? selectionTarget?.assemblyNodeId
    if (!assemblyNodeId) {
      return
    }

    const proxyObjectId = metadata?.selectionProxyObjectId ?? object.uuid
    const proxyObject = objectByUuid.get(proxyObjectId)
    if (!proxyObject) {
      return
    }

    const bindingIds = bindingIdsByAssemblyNodeId.get(assemblyNodeId) ?? new Set<string>()
    bindingIds.add(proxyObject.uuid)
    bindingIdsByAssemblyNodeId.set(assemblyNodeId, bindingIds)
    sceneBindings.byObject3DUuid[proxyObject.uuid] = proxyObject
  })

  bindingIdsByAssemblyNodeId.forEach((bindingIds, assemblyNodeId) => {
    sceneBindings.byAssemblyNodeId[assemblyNodeId] = Array.from(bindingIds)
  })

  return {
    selectionIndex,
    sceneBindings,
  }
}

/**
 * 创建子装配体 tab 使用的真实 imported-model 运行时结果。
 *
 * @param input 创建输入。
 * @returns 当前子装配体 tab 的 `ComposedAssemblyResult`。
 * @throws 当 `scopeObjectId` 在源场景中没有对应的作用域对象时。
 * @throws 当 `scopeObjectId` 在源 hierarchy 中没有对应的作用域节点时。
 */
export function createPuppyScopedAssemblyResult(
  input: PuppyScopedAssemblyResultInput,
): ComposedAssemblyResult {
  const sourceScopeObject = findObjectByUuid(input.sourceResult.root, input.scopeObjectId)
  if (!sourceScopeObject) {
    throw new Error(`PuppyCAD 离线包 tab 缺少场景作用域对象: ${input.scopeObjectId}`)
  }

  const sourceHierarchy = findHierarchyNodeByObjectId(input.sourceResult.hierarchy, input.scopeObjectId)
  if (!sourceHierarchy) {
    throw new Error(`PuppyCAD 离线包 tab 缺少 hierarchy 作用域节点: ${input.scopeObjectId}`)
  }

  const clonedScopeObject = cloneWithSharedGeometry(sourceScopeObject)
  const uuidMap = new Map<string, string>()
  collectCloneUuidMap(sourceScopeObject, clonedScopeObject, uuidMap)
  const scopedAssemblyNodeIds = new Set<string>()
  collectAssemblyNodeIdsFromHierarchy(sourceHierarchy, scopedAssemblyNodeIds)
  collectAssemblyNodeIdsFromMetadata(sourceScopeObject, scopedAssemblyNodeIds)

  /**
   * 子装配体 tab 表示该装配体自己的编辑上下文，不能继续携带父装配中的放置变换。
   * 否则底部 tab 切换时会看到完整装配坐标下的偏移结果，而不是子装配体本地坐标。
   */
  clonedScopeObject.position.set(0, 0, 0)
  clonedScopeObject.quaternion.set(0, 0, 0, 1)
  clonedScopeObject.scale.set(1, 1, 1)
  clonedScopeObject.updateMatrix()
  clonedScopeObject.matrixWorldNeedsUpdate = true

  rewriteScopedObjectMetadata(clonedScopeObject, uuidMap, scopedAssemblyNodeIds)

  const root = new THREE.Group()
  root.name = 'imported-assembly'
  addObject3DToParent(clonedScopeObject, root)
  root.updateMatrixWorld(true)
  activateImportedModelTopologyHosts(root, input.topologySource)

  const { selectionIndex, sceneBindings } = createPuppyRuntimeIndexesFromMetadata(root)

  return {
    kind: 'composed-assembly',
    fileInfo: input.sourceResult.fileInfo,
    partName: sourceHierarchy.name,
    root,
    hierarchy: cloneScopedHierarchy({
      node: sourceHierarchy,
      parentObjectId: null,
      parentPath: '',
      depth: 0,
      uuidMap,
    }),
    partAssetsByUuid: input.sourceResult.partAssetsByUuid,
    assemblyAssetsByUuid: input.sourceResult.assemblyAssetsByUuid,
    manifest: input.sourceResult.manifest,
    selectionIndex,
    sceneBindings,
    warnings: [...input.sourceResult.warnings],
    sourceAssetKey: `${input.sourceResult.sourceAssetKey ?? input.sourceResult.fileInfo.uuid}:scope:${input.scopeObjectId}`,
  }
}
